// Orthogonal edge routing that steers clear of node boxes.
//
// React Flow's built-in edges route from source to target without knowing where the other nodes
// are, so a long edge can run under a node. This generates a few candidate right-angle routes
// and picks the one that crosses the fewest nodes (then the shortest), which keeps edges
// readable and out from behind the cards.

const CORNER_RADIUS = 14;

const dedupe = (points) =>
  points.filter((point, index) => {
    if (index === 0) return true;
    const previous = points[index - 1];
    return Math.abs(point.x - previous.x) > 0.5 || Math.abs(point.y - previous.y) > 0.5;
  });

const segmentsOf = (points) =>
  points.slice(1).map((point, index) => ({
    x1: points[index].x,
    y1: points[index].y,
    x2: point.x,
    y2: point.y,
  }));

const segmentHitsRect = (segment, rect) => {
  const left = Math.min(segment.x1, segment.x2);
  const right = Math.max(segment.x1, segment.x2);
  const top = Math.min(segment.y1, segment.y2);
  const bottom = Math.max(segment.y1, segment.y2);

  return right > rect.left && left < rect.right && bottom > rect.top && top < rect.bottom;
};

const scoreRoute = (points, rects) => {
  const segments = segmentsOf(points);
  let hits = 0;

  segments.forEach((segment) => {
    rects.forEach((rect) => {
      if (segmentHitsRect(segment, rect)) hits += 1;
    });
  });

  const length = segments.reduce(
    (total, segment) => total + Math.abs(segment.x2 - segment.x1) + Math.abs(segment.y2 - segment.y1),
    0
  );

  return { hits, length, score: hits * 1e6 + length };
};

const roundedPath = (points, radius = CORNER_RADIUS) => {
  if (points.length === 0) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

  let path = `M ${points[0].x} ${points[0].y}`;

  for (let index = 1; index < points.length - 1; index += 1) {
    const previous = points[index - 1];
    const current = points[index];
    const next = points[index + 1];

    const inLength = Math.hypot(current.x - previous.x, current.y - previous.y) || 1;
    const outLength = Math.hypot(next.x - current.x, next.y - current.y) || 1;
    const corner = Math.min(radius, inLength / 2, outLength / 2);

    const start = {
      x: current.x - ((current.x - previous.x) / inLength) * corner,
      y: current.y - ((current.y - previous.y) / inLength) * corner,
    };
    const end = {
      x: current.x + ((next.x - current.x) / outLength) * corner,
      y: current.y + ((next.y - current.y) / outLength) * corner,
    };

    path += ` L ${start.x} ${start.y} Q ${current.x} ${current.y} ${end.x} ${end.y}`;
  }

  const last = points[points.length - 1];
  path += ` L ${last.x} ${last.y}`;

  return path;
};

/**
 * Route an edge left-to-right (the graph's direction) as a right-angle polyline that avoids the
 * given rectangles.
 *
 * @param {object} options
 * @param {number} options.sourceX
 * @param {number} options.sourceY
 * @param {number} options.targetX
 * @param {number} options.targetY
 * @param {{left: number, right: number, top: number, bottom: number}[]} [options.rects]
 *   Obstacles (the other nodes' boxes).
 * @param {number} [options.offset] How far to step out of the source / into the target.
 * @returns {{path: string, points: {x: number, y: number}[], hits: number}}
 */
export const buildOrthogonalEdgePath = ({
  sourceX,
  sourceY,
  targetX,
  targetY,
  rects = [],
  offset = 48,
  exitOffset = 0,
  entryOffset = 0,
  exitLane = 0,
  entryLane = 0,
}) => {
  const obstacles = Array.isArray(rects) ? rects : [];

  // Edges that share a handle leave/enter along separate lanes, so their origins stay visible.
  // The lane offset is applied just outside the handle, then a short jog lands on the handle.
  // Each edge gets its own corridor just outside the handle, so several edges into one node
  // keep separate vertical runs instead of stacking on the same line.
  const startX = sourceX + 22 + Math.min(exitLane, 6) * 14;
  const startY = sourceY + exitOffset;
  const endX = targetX - 22 - Math.min(entryLane, 6) * 14;
  const endY = targetY + entryOffset;

  const midX = (startX + endX) / 2;
  const afterSource = startX + offset;
  const beforeTarget = endX - offset;

  const corridorTop = Math.min(startY, endY, ...obstacles.map((rect) => rect.top)) - offset;
  const corridorBottom = Math.max(startY, endY, ...obstacles.map((rect) => rect.bottom)) + offset;

  const assemble = (core) =>
    dedupe([
      { x: sourceX, y: sourceY },
      { x: startX, y: sourceY },
      ...core,
      { x: endX, y: targetY },
      { x: targetX, y: targetY },
    ]);

  const candidates = [
    // Straight Z: vertical run at the midpoint.
    assemble([
      { x: startX, y: startY },
      { x: midX, y: startY },
      { x: midX, y: endY },
      { x: endX, y: endY },
    ]),
    // Step out of the source first.
    assemble([
      { x: startX, y: startY },
      { x: afterSource, y: startY },
      { x: afterSource, y: endY },
      { x: endX, y: endY },
    ]),
    // Approach the target from the left.
    assemble([
      { x: startX, y: startY },
      { x: beforeTarget, y: startY },
      { x: beforeTarget, y: endY },
      { x: endX, y: endY },
    ]),
    // Travel along the corridor above the graph.
    assemble([
      { x: startX, y: startY },
      { x: afterSource, y: startY },
      { x: afterSource, y: corridorTop },
      { x: beforeTarget, y: corridorTop },
      { x: beforeTarget, y: endY },
      { x: endX, y: endY },
    ]),
    // …or below it.
    assemble([
      { x: startX, y: startY },
      { x: afterSource, y: startY },
      { x: afterSource, y: corridorBottom },
      { x: beforeTarget, y: corridorBottom },
      { x: beforeTarget, y: endY },
      { x: endX, y: endY },
    ]),
  ];

  let best = null;

  candidates.forEach((points) => {
    const cleaned = dedupe(points);
    const scored = scoreRoute(cleaned, obstacles);
    if (!best || scored.score < best.score) {
      best = { ...scored, points: cleaned };
    }
  });

  return {
    path: roundedPath(best.points),
    points: best.points,
    hits: best.hits,
  };
};

export default buildOrthogonalEdgePath;
